import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { KeyRound, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChangePasswordCard() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const tt = (en, es) => (language === 'es' ? es : en);

  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setCurrent(''); setNext(''); setConfirm(''); };

  const submit = async () => {
    if (!current || !next) {
      toast.error(tt('Both fields are required', 'Ambos campos son requeridos'));
      return;
    }
    if (next.length < 6) {
      toast.error(tt('Password must be at least 6 characters', 'La contraseña debe tener al menos 6 caracteres'));
      return;
    }
    if (next !== confirm) {
      toast.error(tt("Passwords don't match", 'Las contraseñas no coinciden'));
      return;
    }
    if (current === next) {
      toast.error(tt('New password must be different', 'La nueva contraseña debe ser diferente'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next, language },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(tt('Password updated. A confirmation email was sent.', 'Contraseña actualizada. Se envió un correo de confirmación.'));
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || tt('Failed to update password', 'Error al actualizar contraseña'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="mb-4 border-amber-200/60 bg-gradient-to-br from-white to-amber-50/30" data-testid="change-password-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
              <KeyRound className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-base text-[#0B192C]">
                {tt('Account Security', 'Seguridad de la Cuenta')}
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                {tt('Update your password to keep your account secure.', 'Actualiza tu contraseña para mantener tu cuenta segura.')}
              </p>
            </div>
          </div>
          {!open && (
            <Button
              variant="outline"
              onClick={() => setOpen(true)}
              className="gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-100"
              data-testid="open-change-password-btn"
            >
              <KeyRound className="w-4 h-4" />
              {tt('Change Password', 'Cambiar Contraseña')}
            </Button>
          )}
        </div>

        {open && (
          <div className="mt-5 space-y-3 border-t border-amber-200/50 pt-4">
            <div>
              <Label>{tt('Current password', 'Contraseña actual')}</Label>
              <div className="relative mt-1">
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  placeholder="••••••••"
                  data-testid="current-password-input"
                />
                <button type="button" onClick={() => setShowCurrent(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>{tt('New password', 'Nueva contraseña')}</Label>
              <div className="relative mt-1">
                <Input
                  type={showNext ? 'text' : 'password'}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  placeholder={tt('Min 6 characters', 'Mínimo 6 caracteres')}
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShowNext(s => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>{tt('Confirm new password', 'Confirmar nueva contraseña')}</Label>
              <Input
                type={showNext ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="mt-1"
                data-testid="confirm-password-input"
              />
              {confirm && confirm !== next && (
                <p className="text-xs text-red-600 mt-1">{tt("Passwords don't match", 'Las contraseñas no coinciden')}</p>
              )}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button
                variant="outline"
                onClick={() => { reset(); setOpen(false); }}
                disabled={submitting}
                data-testid="cancel-change-password-btn"
              >
                {tt('Cancel', 'Cancelar')}
              </Button>
              <Button
                onClick={submit}
                disabled={submitting || !current || !next || next.length < 6 || next !== confirm}
                className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                data-testid="submit-change-password-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                {tt('Update Password', 'Actualizar Contraseña')}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
